import { useContext } from 'react'
import { DateTime } from 'luxon'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'
import type { UseFormPayload } from '@domain-group/react-hooks'

import useCampaign, { useCampaignForm } from '@hooks/useCampaign'

import { calculateCampaignDuration } from '@functions/campaign'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'EditCampaignDatesModal'

type ChangeDate = {
  entityState: UseFormPayload['entityState'],
  key: 'end_date' | 'start_date',
  setEntityState: UseFormPayload['setEntityState'],
  value: string,
}

const changeDate = (params: ChangeDate) => {
  const {
    entityState, key, setEntityState, value,
  } = params

  // Start Date
  if (key === 'start_date'){
    const startDateObj = DateTime.fromFormat(value, 'yyyy-MM-dd')
    const endDateObj = startDateObj.plus({ weeks: 4 })

    setEntityState({
      campaign_duration: '4w',
      end_date: endDateObj.toFormat('yyyy-MM-dd'),
      start_date: value,
    })

    return
  }

  // End Date
  setEntityState({
    campaign_duration: calculateCampaignDuration({
      startDateString: entityState.start_date,
      endDateString: value,
    }),
    end_date: value,
  })
}

type ModalCallbacks = {
  closeModal: () => void,
  closeAllModals: () => void,
  updateCampaign: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const EditCampaignDatesModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, closeAllModals, updateCampaign } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const initCampaign = digObject(modalPayload, 'campaign', {})

  const successFn = digObject(modalPayload, 'callbacks.successFn')

  const { campaign } = useCampaign(initCampaign)

  const {
    callbacks: {
      updateCampaign: updateFn,
    },
    updating,
  } = useCampaign(campaign)

  const {
    entityState,
    entityState: {
      end_date,
      start_date,
    },
    errors,
    saveEnabled,
    setEntityState,
  } = useCampaignForm(campaign, { validateOn: 'change' })

  const createOrUpdateCampaignPayload = {
    callbacks: {
      action: updateFn,
      afterAction: () => {
        if (closeAllModals){
          closeAllModals()
        }
        closeModal()

        if (successFn) return successFn(start_date)

        return undefined
      },
    },
    entityParams: entityState,
    toastText: 'Campaign Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Edit" titleSecondLine="Campaign Dates" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Start Date" errorMessage={errors.start_date}>
            <input
              onChange={e => changeDate({
                entityState,
                key: 'start_date',
                setEntityState,
                value: e.target.value,
              })}
              name="start_date"
              type="date"
              value={start_date}
            />
          </Form.Field>

          <Form.Field label="* End Date" errorMessage={errors.end_date} marginTop="large">
            <input
              onChange={e => changeDate({
                entityState,
                key: 'end_date',
                setEntityState,
                value: e.target.value,
              })}
              min={start_date}
              name="end_date"
              type="date"
              value={end_date}
            />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          loadingBubbleColor="white"
          // @ts-expect-error TS2345
          onClick={() => updateCampaign(createOrUpdateCampaignPayload)}
          size="large"
        >
          {successFn ? 'Approve Quote' : 'Update Campaign'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <EditCampaignDatesModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
